// NEW_FILE_JUSTIFIED: Pure runtime validator for IBuilderField validation rules — no side effects, no throwing
import type {
  IBuilderField,
  IFieldValidationRules,
  IValidationContext,
} from '@/@types/insuranceBuilder';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// ── Emptiness helpers ────────────────────────────────────────────────────────

function isTextLike(field: IBuilderField): boolean {
  return ['text', 'textarea', 'email', 'phone', 'postalCode'].includes(field.fieldType);
}

function isEmpty(field: IBuilderField, value: unknown, ctx?: IValidationContext): boolean {
  if (field.fieldType === 'multiSelect') {
    return !Array.isArray(value) || value.length === 0;
  }
  if (field.fieldType === 'checkbox') {
    return value === undefined || value === null;
  }
  if (field.fieldType === 'file') {
    return !ctx?.files || ctx.files.length === 0;
  }
  if (isTextLike(field)) {
    return typeof value !== 'string' || value.trim() === '';
  }
  return value === undefined || value === null;
}

// ── Rule validators ──────────────────────────────────────────────────────────

function validateTextLike(
  rules: IFieldValidationRules,
  value: unknown,
  isEmail: boolean
): string | null {
  const str = typeof value === 'string' ? value : '';

  if (isEmail && !EMAIL_RE.test(str)) {
    return 'Please enter a valid email address.';
  }

  if (rules.minLength !== undefined && str.length < rules.minLength) {
    return `Must be at least ${rules.minLength} characters.`;
  }
  if (rules.maxLength !== undefined && str.length > rules.maxLength) {
    return `Must be at most ${rules.maxLength} characters.`;
  }

  if (rules.regex !== undefined) {
    let re: RegExp | null = null;
    try {
      re = new RegExp(rules.regex);
    } catch {
      // Invalid regex — silently skip
      re = null;
    }
    if (re !== null && !re.test(str)) {
      return rules.regexMessage ?? 'Value does not match the required format.';
    }
  }

  return null;
}

function validateNumber(rules: IFieldValidationRules, value: unknown): string | null {
  const n = typeof value === 'number' ? value : Number(value);

  if (!Number.isFinite(n)) return 'Please enter a valid number.';

  if (rules.integerOnly && !Number.isInteger(n)) {
    return 'Please enter a whole number.';
  }
  if (rules.min !== undefined && n < rules.min) {
    return `Must be at least ${rules.min}.`;
  }
  if (rules.max !== undefined && n > rules.max) {
    return `Must be at most ${rules.max}.`;
  }

  return null;
}

function validateDate(rules: IFieldValidationRules, value: unknown): string | null {
  const ts =
    typeof value === 'string' || typeof value === 'number' ? Date.parse(String(value)) : NaN;

  if (!Number.isFinite(ts)) return 'Please enter a valid date.';

  const now = Date.now();

  if (rules.mustBePast && ts >= now) return 'Date must be in the past.';
  if (rules.mustBeFuture && ts <= now) return 'Date must be in the future.';

  if (rules.minDate !== undefined) {
    const min = Date.parse(rules.minDate);
    if (Number.isFinite(min) && ts < min) {
      return `Date must be on or after ${rules.minDate}.`;
    }
  }
  if (rules.maxDate !== undefined) {
    const max = Date.parse(rules.maxDate);
    if (Number.isFinite(max) && ts > max) {
      return `Date must be on or before ${rules.maxDate}.`;
    }
  }

  return null;
}

function mimeMatches(fileType: string, pattern: string): boolean {
  if (pattern === fileType) return true;
  // Support "image/*" style wildcards
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -1); // "image/"
    return fileType.startsWith(prefix);
  }
  return false;
}

function validateFile(rules: IFieldValidationRules, ctx?: IValidationContext): string | null {
  const files = ctx?.files ?? [];

  if (rules.maxSizeMB !== undefined) {
    const maxBytes = rules.maxSizeMB * 1024 * 1024;
    for (const file of files) {
      if (file.size > maxBytes) {
        return `File must be smaller than ${rules.maxSizeMB} MB.`;
      }
    }
  }

  if (rules.acceptedMimeTypes !== undefined && rules.acceptedMimeTypes.length > 0) {
    for (const file of files) {
      const accepted = rules.acceptedMimeTypes.some(pattern => mimeMatches(file.type, pattern));
      if (!accepted) {
        return `File type "${file.type}" is not accepted.`;
      }
    }
  }

  return null;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Validates a single field value against its rules.
 * Returns an error string, or null if the value is valid.
 */
export function validateField(
  field: IBuilderField,
  value: unknown,
  ctx?: IValidationContext
): string | null {
  const rules = field.validation ?? {};
  const empty = isEmpty(field, value, ctx);

  // Required check fires before rule checks
  if (empty) {
    return field.required ? `${field.label} is required.` : null;
  }

  // Optional + empty already returned null above; if we're here the value is present
  switch (field.fieldType) {
    case 'text':
    case 'textarea':
    case 'phone':
    case 'postalCode':
      return validateTextLike(rules, value, false);

    case 'email':
      return validateTextLike(rules, value, true);

    case 'number':
      return validateNumber(rules, value);

    case 'date':
      return validateDate(rules, value);

    case 'file':
      return validateFile(rules, ctx);

    case 'select':
    case 'multiSelect':
    case 'checkbox':
      return null;

    default:
      return null;
  }
}

/**
 * Validates all fields and returns a map of fieldKey → error string.
 * Only fields with errors are included in the returned map.
 */
export function validateAllFields(
  fields: IBuilderField[],
  values: Record<string, unknown>,
  filesByField?: Record<string, File[]>
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const ctx: IValidationContext | undefined = filesByField
      ? { files: filesByField[field.fieldKey] ?? [] }
      : undefined;
    const error = validateField(field, values[field.fieldKey], ctx);
    if (error !== null) {
      errors[field.fieldKey] = error;
    }
  }

  return errors;
}

/**
 * Returns the labels of all required fields that are currently empty.
 * Used as a soft-warning helper when new required fields are added to existing policies.
 */
export function findMissingRequiredLabels(
  fields: IBuilderField[],
  values: Record<string, unknown>,
  filesByField?: Record<string, File[]>
): string[] {
  const missing: string[] = [];

  for (const field of fields) {
    if (!field.required) continue;
    const ctx: IValidationContext = { files: filesByField?.[field.fieldKey] ?? [] };
    if (isEmpty(field, values[field.fieldKey], ctx)) {
      missing.push(field.label);
    }
  }

  return missing;
}
